/**
 * COMPREHENSIVE GOLF BALL DATABASE
 * 
 * Updated: October 2025
 * Version: 1.0.0
 * 
 * MAINTENANCE:
 * - Update quarterly with new releases
 * - Check manufacturer websites for latest models
 * - Update prices annually
 */

export interface GolfBall {
  id: string;
  brand: string;
  model: string;
  year: number;
  
  // Performance
  compression: number; // 0-120 (lower = softer feel, better for slower swing speeds)
  dimples: number;
  construction: '2-piece' | '3-piece' | '4-piece' | '5-piece';
  cover: 'Surlyn' | 'Urethane' | 'Ionomer' | 'Cast Urethane' | 'Hybrid Cover';
  
  // Characteristics
  spinRate: 'low' | 'mid' | 'mid-high' | 'high' | 'very-high';
  feel: 'firm' | 'medium' | 'soft' | 'very-soft';
  durability: number; // 1-10 (10 = most durable)
  
  // Target Player
  swingSpeedRange: {
    min: number; // mph
    max: number; // mph
  };
  handicapRange: {
    min: number;
    max: number;
  };
  skillLevel: 'beginner' | 'intermediate' | 'advanced' | 'tour';
  
  // Pricing
  pricePerDozen: {
    min: number;
    max: number;
    typical: number;
  };
  
  // Features
  category: 'tour' | 'tour-value' | 'performance' | 'distance' | 'soft-feel' | 'value';
  keyFeatures: string[];
  bestFor: string[];
  notIdealFor: string[];
  
  // Meta
  popularityScore: number; // 1-10
  available: boolean;
}

export const GOLF_BALLS: GolfBall[] = [
  // ==================== TOUR PERFORMANCE ====================
  {
    id: 'titleist-prov1-2024',
    brand: 'Titleist',
    model: 'Pro V1',
    year: 2024,
    compression: 90,
    dimples: 388,
    construction: '3-piece',
    cover: 'Cast Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 7,
    swingSpeedRange: { min: 95, max: 115 },
    handicapRange: { min: 0, max: 15 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 50, max: 60, typical: 55 },
    category: 'tour',
    keyFeatures: [
      'Tour-proven performance',
      'High short game spin',
      'Penetrating ball flight',
      'Consistent quality'
    ],
    bestFor: [
      'Skilled players wanting maximum control',
      'Those who value greenside spin',
      'Competitive golfers',
      'Faster swing speeds (95+ mph)'
    ],
    notIdealFor: [
      'Beginners (wasted performance)',
      'Slower swing speeds (under 90 mph)',
      'Budget-conscious players',
      'Those who lose balls frequently'
    ],
    popularityScore: 10,
    available: true
  },
  
  {
    id: 'titleist-prov1x-2024',
    brand: 'Titleist',
    model: 'Pro V1x',
    year: 2024,
    compression: 100,
    dimples: 348,
    construction: '4-piece',
    cover: 'Cast Urethane',
    spinRate: 'very-high',
    feel: 'medium',
    durability: 7,
    swingSpeedRange: { min: 100, max: 120 },
    handicapRange: { min: 0, max: 10 },
    skillLevel: 'tour',
    pricePerDozen: { min: 50, max: 60, typical: 55 },
    category: 'tour',
    keyFeatures: [
      'Lower flight than Pro V1',
      'Maximum spin on wedges',
      'Firmer feel for feedback',
      'Preferred by tour pros'
    ],
    bestFor: [
      'Fast swing speeds (100+ mph)',
      'Low handicappers',
      'Those wanting maximum spin',
      'Players who compress the ball well'
    ],
    notIdealFor: [
      'Slower swing speeds',
      'Players wanting softest feel',
      'Budget players',
      'High handicappers'
    ],
    popularityScore: 9,
    available: true
  },

  {
    id: 'taylormade-tp5-2024',
    brand: 'TaylorMade',
    model: 'TP5',
    year: 2024,
    compression: 85,
    dimples: 322,
    construction: '5-piece',
    cover: 'Cast Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 8,
    swingSpeedRange: { min: 90, max: 110 },
    handicapRange: { min: 0, max: 15 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 45, max: 55, typical: 50 },
    category: 'tour',
    keyFeatures: [
      '5-layer construction',
      'Speed Wrapped Core',
      'High launch, low spin with driver',
      'High spin around greens'
    ],
    bestFor: [
      'Players wanting tour ball at better value than Pro V1',
      'Those with 90-110 mph swing speed',
      'Players who value feel',
      'Versatile performance seekers'
    ],
    notIdealFor: [
      'Very slow swing speeds (under 85)',
      'Budget players',
      'Those who lose balls often'
    ],
    popularityScore: 9,
    available: true
  },

  {
    id: 'taylormade-tp5x-2024',
    brand: 'TaylorMade',
    model: 'TP5x',
    year: 2024,
    compression: 97,
    dimples: 322,
    construction: '5-piece',
    cover: 'Cast Urethane',
    spinRate: 'very-high',
    feel: 'medium',
    durability: 8,
    swingSpeedRange: { min: 100, max: 120 },
    handicapRange: { min: 0, max: 12 },
    skillLevel: 'tour',
    pricePerDozen: { min: 45, max: 55, typical: 50 },
    category: 'tour',
    keyFeatures: [
      'Firmer than TP5',
      'Lower flight',
      'Maximum distance for fast swingers',
      'Tour-level spin'
    ],
    bestFor: [
      'Fast swing speeds (100+ mph)',
      'Players wanting lower flight',
      'Maximum distance seekers with control',
      'Competitive players'
    ],
    notIdealFor: [
      'Slower swing speeds',
      'Players wanting soft feel',
      'High handicappers',
      'Budget conscious'
    ],
    popularityScore: 8,
    available: true
  },

  // ==================== TOUR VALUE ====================
  {
    id: 'callaway-chrome-soft-2024',
    brand: 'Callaway',
    model: 'Chrome Soft',
    year: 2024,
    compression: 75,
    dimples: 332,
    construction: '4-piece',
    cover: 'Urethane',
    spinRate: 'mid-high',
    feel: 'very-soft',
    durability: 7,
    swingSpeedRange: { min: 85, max: 105 },
    handicapRange: { min: 5, max: 20 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 45, max: 55, typical: 48 },
    category: 'tour-value',
    keyFeatures: [
      'Ultra-soft feel',
      'High launch',
      'Tour-level performance',
      'Excellent value'
    ],
    bestFor: [
      'Mid handicappers wanting tour ball',
      'Players with moderate swing speeds',
      'Those who value soft feel',
      'Best bang-for-buck in tour category'
    ],
    notIdealFor: [
      'Very fast swing speeds (may compress too much)',
      'Players wanting firmest feedback',
      'Absolute beginners'
    ],
    popularityScore: 10,
    available: true
  },

  {
    id: 'callaway-chrome-soft-x-2024',
    brand: 'Callaway',
    model: 'Chrome Soft X',
    year: 2024,
    compression: 90,
    dimples: 332,
    construction: '4-piece',
    cover: 'Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 7,
    swingSpeedRange: { min: 95, max: 115 },
    handicapRange: { min: 0, max: 15 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 45, max: 55, typical: 48 },
    category: 'tour-value',
    keyFeatures: [
      'Firmer than Chrome Soft',
      'Lower flight',
      'More workability',
      'Great for faster swingers'
    ],
    bestFor: [
      'Faster swing speeds',
      'Low to mid handicappers',
      'Players wanting workability',
      'Tour performance at good value'
    ],
    notIdealFor: [
      'Slower swing speeds',
      'Those wanting softest feel',
      'High handicappers',
      'Budget players'
    ],
    popularityScore: 9,
    available: true
  },

  {
    id: 'srixon-zstar-2024',
    brand: 'Srixon',
    model: 'Z-STAR',
    year: 2024,
    compression: 88,
    dimples: 338,
    construction: '3-piece',
    cover: 'Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 8,
    swingSpeedRange: { min: 90, max: 110 },
    handicapRange: { min: 0, max: 15 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 40, max: 50, typical: 43 },
    category: 'tour-value',
    keyFeatures: [
      'Excellent value tour ball',
      'Great greenside spin',
      'Soft feel',
      'Underrated performance'
    ],
    bestFor: [
      'Value-conscious skilled players',
      'Those wanting tour ball under $45',
      'Mid to low handicappers',
      'Players who value feel'
    ],
    notIdealFor: [
      'Very slow swing speeds',
      'Those wanting premium branding',
      'Absolute beginners'
    ],
    popularityScore: 7,
    available: true
  },

  {
    id: 'bridgestone-tour-bx-2024',
    brand: 'Bridgestone',
    model: 'Tour B X',
    year: 2024,
    compression: 95,
    dimples: 330,
    construction: '3-piece',
    cover: 'Urethane',
    spinRate: 'high',
    feel: 'medium',
    durability: 8,
    swingSpeedRange: { min: 100, max: 120 },
    handicapRange: { min: 0, max: 12 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 42, max: 52, typical: 45 },
    category: 'tour-value',
    keyFeatures: [
      'Tiger Woods ball',
      'Gradational core',
      'Great value tour ball',
      'Excellent durability'
    ],
    bestFor: [
      'Fast swing speeds',
      'Players wanting tour ball value',
      'Those seeking distance and control',
      'Bridgestone loyalists'
    ],
    notIdealFor: [
      'Slower swing speeds',
      'Players wanting softest feel',
      'Beginners'
    ],
    popularityScore: 7,
    available: true
  },

  // ==================== PERFORMANCE ====================
  {
    id: 'titleist-tour-soft-2024',
    brand: 'Titleist',
    model: 'Tour Soft',
    year: 2024,
    compression: 65,
    dimples: 342,
    construction: '3-piece',
    cover: 'Ionomer',
    spinRate: 'mid',
    feel: 'soft',
    durability: 8,
    swingSpeedRange: { min: 75, max: 95 },
    handicapRange: { min: 10, max: 30 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 30, max: 40, typical: 35 },
    category: 'performance',
    keyFeatures: [
      'Titleist quality at mid price',
      'Soft feel for slower speeds',
      'Good greenside control',
      'Excellent value'
    ],
    bestFor: [
      'Mid to high handicappers',
      'Slower to moderate swing speeds',
      'Players wanting Titleist brand affordably',
      'Those transitioning from distance balls'
    ],
    notIdealFor: [
      'Very fast swing speeds',
      'Tour-level spin seekers',
      'Those on tightest budget'
    ],
    popularityScore: 9,
    available: true
  },

  {
    id: 'callaway-supersoft-2024',
    brand: 'Callaway',
    model: 'Supersoft',
    year: 2024,
    compression: 38,
    dimples: 332,
    construction: '2-piece',
    cover: 'Hybrid Cover',
    spinRate: 'low',
    feel: 'very-soft',
    durability: 9,
    swingSpeedRange: { min: 70, max: 90 },
    handicapRange: { min: 15, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 25, max: 35, typical: 28 },
    category: 'soft-feel',
    keyFeatures: [
      'Extremely soft feel',
      'Low compression for easy distance',
      'Straight flight',
      'Great value'
    ],
    bestFor: [
      'Slower swing speeds',
      'High handicappers',
      'Seniors',
      'Players wanting soft feel on budget'
    ],
    notIdealFor: [
      'Fast swing speeds (will compress too much)',
      'Those wanting maximum spin',
      'Competitive players'
    ],
    popularityScore: 10,
    available: true
  },

  {
    id: 'taylormade-soft-response-2024',
    brand: 'TaylorMade',
    model: 'Soft Response',
    year: 2024,
    compression: 50,
    dimples: 322,
    construction: '3-piece',
    cover: 'Ionomer',
    spinRate: 'mid',
    feel: 'soft',
    durability: 8,
    swingSpeedRange: { min: 75, max: 95 },
    handicapRange: { min: 12, max: 30 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 25, max: 35, typical: 30 },
    category: 'performance',
    keyFeatures: [
      'Soft feel with control',
      'SpeedFlex material',
      'Good greenside spin',
      'Excellent value'
    ],
    bestFor: [
      'Mid handicappers',
      'Moderate swing speeds',
      'Players wanting feel and performance',
      'Value seekers'
    ],
    notIdealFor: [
      'Very fast swing speeds',
      'Tour-level spin needs',
      'Absolute beginners'
    ],
    popularityScore: 8,
    available: true
  },

  {
    id: 'bridgestone-e6-2024',
    brand: 'Bridgestone',
    model: 'e6',
    year: 2024,
    compression: 44,
    dimples: 326,
    construction: '2-piece',
    cover: 'Surlyn',
    spinRate: 'low',
    feel: 'soft',
    durability: 9,
    swingSpeedRange: { min: 70, max: 95 },
    handicapRange: { min: 15, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 25, max: 35, typical: 28 },
    category: 'soft-feel',
    keyFeatures: [
      'Anti-slice technology',
      'Soft compression',
      'Straight flight bias',
      'Great for slice fighters'
    ],
    bestFor: [
      'Players fighting a slice',
      'Slower swing speeds',
      'High handicappers',
      'Those wanting straighter shots'
    ],
    notIdealFor: [
      'Fast swing speeds',
      'Low handicappers',
      'Those wanting maximum spin'
    ],
    popularityScore: 8,
    available: true
  },

  {
    id: 'srixon-soft-feel-2024',
    brand: 'Srixon',
    model: 'Soft Feel',
    year: 2024,
    compression: 60,
    dimples: 338,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'mid',
    feel: 'soft',
    durability: 9,
    swingSpeedRange: { min: 70, max: 95 },
    handicapRange: { min: 12, max: 36 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 20, max: 28, typical: 23 },
    category: 'soft-feel',
    keyFeatures: [
      'Excellent value',
      'Soft feel',
      'Decent greenside control',
      'Very durable'
    ],
    bestFor: [
      'Value-conscious players',
      'Mid to high handicappers',
      'Moderate swing speeds',
      'Those wanting soft feel cheaply'
    ],
    notIdealFor: [
      'Fast swing speeds',
      'Tour-level performance needs',
      'Maximum spin seekers'
    ],
    popularityScore: 8,
    available: true
  },

  // ==================== DISTANCE ====================
  {
    id: 'titleist-velocity-2024',
    brand: 'Titleist',
    model: 'Velocity',
    year: 2024,
    compression: 65,
    dimples: 350,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'firm',
    durability: 9,
    swingSpeedRange: { min: 85, max: 105 },
    handicapRange: { min: 10, max: 28 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 30, max: 40, typical: 35 },
    category: 'distance',
    keyFeatures: [
      'Maximum ball speed',
      'Low spin for distance',
      'Titleist quality',
      'Penetrating flight'
    ],
    bestFor: [
      'Players prioritizing distance',
      'Those with slice tendency',
      'Windy conditions',
      'Firm fairways'
    ],
    notIdealFor: [
      'Players wanting soft feel',
      'Those needing greenside spin',
      'Slow swing speeds'
    ],
    popularityScore: 8,
    available: true
  },

  {
    id: 'callaway-warbird-2024',
    brand: 'Callaway',
    model: 'Warbird',
    year: 2024,
    compression: 75,
    dimples: 302,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'firm',
    durability: 9,
    swingSpeedRange: { min: 80, max: 100 },
    handicapRange: { min: 15, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 20, max: 28, typical: 23 },
    category: 'distance',
    keyFeatures: [
      'Maximum distance focus',
      'Large core for speed',
      'Excellent value',
      'Very durable'
    ],
    bestFor: [
      'Distance seekers on budget',
      'High handicappers',
      'Those who lose balls',
      'Straight distance priority'
    ],
    notIdealFor: [
      'Players wanting feel',
      'Those needing spin control',
      'Low handicappers'
    ],
    popularityScore: 7,
    available: true
  },

  {
    id: 'taylormade-distance-plus-2024',
    brand: 'TaylorMade',
    model: 'Distance+',
    year: 2024,
    compression: 70,
    dimples: 342,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'medium',
    durability: 9,
    swingSpeedRange: { min: 75, max: 100 },
    handicapRange: { min: 15, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 20, max: 28, typical: 23 },
    category: 'distance',
    keyFeatures: [
      'REACT Core technology',
      'Low spin for straighter shots',
      'Good value',
      'Consistent performance'
    ],
    bestFor: [
      'Budget-conscious distance seekers',
      'High handicappers',
      'Moderate swing speeds',
      'Those prioritizing value'
    ],
    notIdealFor: [
      'Players wanting tour performance',
      'Those needing maximum spin',
      'Very fast swing speeds'
    ],
    popularityScore: 7,
    available: true
  },

  // ==================== VALUE ====================
  {
    id: 'wilson-duo-soft-2024',
    brand: 'Wilson',
    model: 'Duo Soft+',
    year: 2024,
    compression: 35,
    dimples: 302,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'very-soft',
    durability: 8,
    swingSpeedRange: { min: 65, max: 90 },
    handicapRange: { min: 18, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 18, max: 25, typical: 20 },
    category: 'value',
    keyFeatures: [
      'Softest feel in class',
      'Lowest compression',
      'Great for seniors/ladies',
      'Best value soft ball'
    ],
    bestFor: [
      'Very slow swing speeds',
      'Seniors and ladies',
      'Budget players wanting soft feel',
      'Those losing balls frequently'
    ],
    notIdealFor: [
      'Fast swing speeds',
      'Players wanting control',
      'Competitive golfers'
    ],
    popularityScore: 8,
    available: true
  },

  {
    id: 'maxfli-softfli-2024',
    brand: 'Maxfli',
    model: 'SoftFli',
    year: 2024,
    compression: 35,
    dimples: 332,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'very-soft',
    durability: 7,
    swingSpeedRange: { min: 65, max: 85 },
    handicapRange: { min: 20, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 15, max: 22, typical: 18 },
    category: 'value',
    keyFeatures: [
      'Dick\'s Sporting Goods exclusive',
      'Extremely soft',
      'Great budget option',
      'Consistent quality'
    ],
    bestFor: [
      'Budget players',
      'Slower swing speeds',
      'Those who lose balls',
      'Soft feel preference on tight budget'
    ],
    notIdealFor: [
      'Fast swing speeds',
      'Skilled players',
      'Those wanting online availability'
    ],
    popularityScore: 6,
    available: true
  },

  {
    id: 'vice-pro-2024',
    brand: 'Vice',
    model: 'Pro',
    year: 2024,
    compression: 90,
    dimples: 318,
    construction: '3-piece',
    cover: 'Cast Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 7,
    swingSpeedRange: { min: 90, max: 110 },
    handicapRange: { min: 5, max: 20 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 35, max: 40, typical: 35 },
    category: 'tour-value',
    keyFeatures: [
      'Direct-to-consumer pricing',
      'Tour-level performance',
      'Excellent value',
      'Unique colors available'
    ],
    bestFor: [
      'Value-conscious skilled players',
      'Those comfortable buying online',
      'Players wanting tour ball cheap',
      'Mid handicappers'
    ],
    notIdealFor: [
      'Those wanting to buy in stores',
      'Absolute beginners',
      'Traditionalists'
    ],
    popularityScore: 8,
    available: true
  },

  {
    id: 'cut-blue-2024',
    brand: 'Cut Golf',
    model: 'Blue',
    year: 2024,
    compression: 85,
    dimples: 336,
    construction: '3-piece',
    cover: 'Cast Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 7,
    swingSpeedRange: { min: 85, max: 105 },
    handicapRange: { min: 8, max: 22 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 30, max: 38, typical: 33 },
    category: 'tour-value',
    keyFeatures: [
      'Direct-to-consumer',
      'Pro V1 alternative',
      'Great value',
      'Good spin'
    ],
    bestFor: [
      'Value hunters',
      'Mid handicappers',
      'Online shoppers',
      'Those wanting urethane on budget'
    ],
    notIdealFor: [
      'Those wanting to buy in stores',
      'Beginners',
      'Very fast swing speeds'
    ],
    popularityScore: 7,
    available: true
  },

  {
    id: 'snell-mtb-x-2024',
    brand: 'Snell',
    model: 'MTB-X',
    year: 2024,
    compression: 90,
    dimples: 360,
    construction: '3-piece',
    cover: 'Cast Urethane',
    spinRate: 'high',
    feel: 'medium',
    durability: 8,
    swingSpeedRange: { min: 95, max: 115 },
    handicapRange: { min: 0, max: 15 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 32, max: 40, typical: 36 },
    category: 'tour-value',
    keyFeatures: [
      'Tour performance',
      'Direct-to-consumer value',
      'High spin',
      'Designed by Dean Snell (former Titleist)'
    ],
    bestFor: [
      'Skilled players on budget',
      'Fast swing speeds',
      'Value-conscious low handicappers',
      'Those wanting tour ball cheap'
    ],
    notIdealFor: [
      'Slower swing speeds',
      'Those wanting in-store purchase',
      'Beginners'
    ],
    popularityScore: 7,
    available: true
  },

  {
    id: 'mizuno-rbz-tour-2024',
    brand: 'Mizuno',
    model: 'RB Tour',
    year: 2024,
    compression: 88,
    dimples: 326,
    construction: '4-piece',
    cover: 'Urethane',
    spinRate: 'high',
    feel: 'soft',
    durability: 7,
    swingSpeedRange: { min: 90, max: 110 },
    handicapRange: { min: 5, max: 18 },
    skillLevel: 'advanced',
    pricePerDozen: { min: 38, max: 48, typical: 42 },
    category: 'tour-value',
    keyFeatures: [
      'Underrated tour ball',
      'Excellent feel',
      'Good value',
      'Micro-dimple technology'
    ],
    bestFor: [
      'Players who value feel',
      'Mid to low handicappers',
      'Mizuno equipment users',
      'Value seekers'
    ],
    notIdealFor: [
      'Those wanting premium branding',
      'Very fast swing speeds',
      'Beginners'
    ],
    popularityScore: 6,
    available: true
  },

  // ==================== BUDGET ====================
  {
    id: 'pinnacle-soft-2024',
    brand: 'Pinnacle',
    model: 'Soft',
    year: 2024,
    compression: 40,
    dimples: 332,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'soft',
    durability: 9,
    swingSpeedRange: { min: 70, max: 95 },
    handicapRange: { min: 18, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 15, max: 22, typical: 18 },
    category: 'value',
    keyFeatures: [
      'Great value',
      'Soft feel on budget',
      'Consistent performance',
      'Widely available'
    ],
    bestFor: [
      'Budget players',
      'High handicappers',
      'Those who lose balls',
      'Practice rounds'
    ],
    notIdealFor: [
      'Skilled players',
      'Those wanting greenside spin',
      'Fast swing speeds'
    ],
    popularityScore: 7,
    available: true
  },

  {
    id: 'top-flite-xl-2024',
    brand: 'Top Flite',
    model: 'XL Distance',
    year: 2024,
    compression: 75,
    dimples: 332,
    construction: '2-piece',
    cover: 'Ionomer',
    spinRate: 'low',
    feel: 'firm',
    durability: 10,
    swingSpeedRange: { min: 75, max: 100 },
    handicapRange: { min: 20, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 12, max: 18, typical: 15 },
    category: 'value',
    keyFeatures: [
      'Rock bottom price',
      'Very durable',
      'Maximum distance focus',
      'Great for practice'
    ],
    bestFor: [
      'Tightest budgets',
      'Those who lose many balls',
      'Practice rounds',
      'Beginners just starting'
    ],
    notIdealFor: [
      'Anyone wanting performance',
      'Players needing feel',
      'Those wanting spin'
    ],
    popularityScore: 6,
    available: true
  },

  {
    id: 'kirkland-signature-2024',
    brand: 'Kirkland',
    model: 'Signature',
    year: 2024,
    compression: 85,
    dimples: 338,
    construction: '3-piece',
    cover: 'Urethane',
    spinRate: 'mid-high',
    feel: 'medium',
    durability: 7,
    swingSpeedRange: { min: 85, max: 105 },
    handicapRange: { min: 8, max: 25 },
    skillLevel: 'intermediate',
    pricePerDozen: { min: 12, max: 18, typical: 15 },
    category: 'tour-value',
    keyFeatures: [
      'Insane value (urethane for $15)',
      'Costco exclusive',
      'Tour-like performance',
      'Cult following'
    ],
    bestFor: [
      'Value hunters',
      'Costco members',
      'Mid handicappers on budget',
      'Those wanting urethane cheap'
    ],
    notIdealFor: [
      'Those without Costco membership',
      'Tour-level consistency needs',
      'Very fast swing speeds'
    ],
    popularityScore: 9,
    available: true
  },

  {
    id: 'nitro-maximum-distance-2024',
    brand: 'Nitro',
    model: 'Maximum Distance',
    year: 2024,
    compression: 70,
    dimples: 332,
    construction: '2-piece',
    cover: 'Surlyn',
    spinRate: 'low',
    feel: 'firm',
    durability: 10,
    swingSpeedRange: { min: 70, max: 95 },
    handicapRange: { min: 25, max: 36 },
    skillLevel: 'beginner',
    pricePerDozen: { min: 8, max: 12, typical: 10 },
    category: 'value',
    keyFeatures: [
      'Cheapest option available',
      'Sold in bulk (2-5 dozen)',
      'Very durable',
      'Practice ball'
    ],
    bestFor: [
      'Absolute beginners',
      'Practice only',
      'Those losing 5+ balls per round',
      'Tightest possible budget'
    ],
    notIdealFor: [
      'Anyone wanting performance',
      'Competitive rounds',
      'Players with any skill'
    ],
    popularityScore: 5,
    available: true
  },
];

// Helper functions
export function getBallsBySwingSpeed(swingSpeed: number): GolfBall[] {
  return GOLF_BALLS.filter(ball => 
    swingSpeed >= ball.swingSpeedRange.min && swingSpeed <= ball.swingSpeedRange.max
  ).sort((a, b) => b.popularityScore - a.popularityScore);
}

export function getBallsByCategory(category: GolfBall['category']): GolfBall[] {
  return GOLF_BALLS.filter(ball => ball.category === category);
}

export function getBallsByBudget(maxPrice: number): GolfBall[] {
  return GOLF_BALLS.filter(ball => ball.pricePerDozen.typical <= maxPrice)
    .sort((a, b) => a.pricePerDozen.typical - b.pricePerDozen.typical);
}

export function getBallsByHandicap(handicap: number): GolfBall[] {
  return GOLF_BALLS.filter(ball =>
    handicap >= ball.handicapRange.min && handicap <= ball.handicapRange.max
  ).sort((a, b) => b.popularityScore - a.popularityScore);
}

export function matchBallToPlayer(profile: {
  swingSpeed?: number;
  handicap: number;
  budget?: number;
  preferredFeel?: 'firm' | 'medium' | 'soft' | 'very-soft';
}): GolfBall[] {
  let candidates = GOLF_BALLS;
  
  // Filter by swing speed if available
  if (profile.swingSpeed) {
    candidates = candidates.filter(ball =>
      profile.swingSpeed! >= ball.swingSpeedRange.min &&
      profile.swingSpeed! <= ball.swingSpeedRange.max
    );
  }
  
  // Filter by handicap
  candidates = candidates.filter(ball =>
    profile.handicap >= ball.handicapRange.min &&
    profile.handicap <= ball.handicapRange.max
  );
  
  // Filter by budget if specified
  if (profile.budget) {
    candidates = candidates.filter(ball =>
      ball.pricePerDozen.typical <= profile.budget!
    );
  }
  
  // Filter by feel preference if specified
  if (profile.preferredFeel) {
    candidates = candidates.filter(ball => ball.feel === profile.preferredFeel);
  }
  
  // Sort by popularity
  return candidates.sort((a, b) => b.popularityScore - a.popularityScore);
}

export const BALL_DATABASE_VERSION = {
  version: '1.0.0',
  lastUpdated: '2025-10-01',
  ballCount: GOLF_BALLS.length,
  nextUpdateDue: '2026-01-01'
};

